import React, { JSX } from 'react';
import Icons from 'components/atoms/Icons';
import {
  getPhaseBadgeLabels,
  getStatusBadgeLabel,
} from 'lib/clinical-trials/api';
import { ClinicalTrial } from 'lib/clinical-trials/types';

type ClinicalTrialsResultProps = {
  trial: ClinicalTrial;
  getInterestUrl?: (nctId: string) => string;
  onLearnMore: (nctId: string) => void;
};

const ClinicalTrialsResult = (
  props: ClinicalTrialsResultProps
): JSX.Element => {
  const { trial, getInterestUrl, onLearnMore } = props;
  const heading = trial.officialTitle || trial.briefTitle;
  const statusLabel = getStatusBadgeLabel(trial.overallStatus);
  const phaseLabels = getPhaseBadgeLabels(trial.phases);
  const interestUrl =
    getInterestUrl?.(trial.nctId) ||
    `${trial.studyUrl}#contacts-locations`;

  return (
    <article className="clinical-trial-result">
      <div className="clinical-trial-result__badges">
        {trial.conditions.map(condition => (
          <span
            key={condition}
            className="clinical-trial-badge clinical-trial-badge--condition"
          >
            {condition}
          </span>
        ))}
        {phaseLabels.map(phase => (
          <span
            key={phase}
            className="clinical-trial-badge clinical-trial-badge--phase"
          >
            {phase}
          </span>
        ))}
        <span
          className={`clinical-trial-badge clinical-trial-badge--status${
            trial.acceptingNewPatients
              ? ' clinical-trial-badge--accepting'
              : ''
          }`}
        >
          {statusLabel}
        </span>
      </div>

      {heading ? (
        <h3 className="osf-display-h3 clinical-trial-result__title">
          <button
            type="button"
            className="clinical-trial-result__title-link"
            onClick={() => onLearnMore(trial.nctId)}
          >
            {heading}
          </button>
        </h3>
      ) : null}

      <div className="clinical-trial-result__meta">
        {trial.principalInvestigator ? (
          <p className="clinical-trial-result__meta-row">
            <Icons id="pld-person" className="clinical-trial-result__icon" />
            <span>
              <strong>Principal Investigator:</strong>{' '}
              {trial.principalInvestigator}
            </span>
          </p>
        ) : null}
        {trial.nctId ? (
          <p className="clinical-trial-result__meta-row">
            <Icons
              id="pld-document-search"
              className="clinical-trial-result__icon"
            />
            <span>
              <strong>ClinicalTrials.gov Identifier:</strong>{' '}
              <a
                href={trial.studyUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="clinical-trial-result__nct-link"
              >
                {trial.nctId}
              </a>
            </span>
          </p>
        ) : null}
      </div>

      <div className="clinical-trial-result__actions">
        <a
          href={interestUrl}
          className="btn-primary no-icon"
          target="_blank"
          rel="noopener noreferrer"
        >
          I&rsquo;m Interested
        </a>
        <button
          type="button"
          className="btn-secondary"
          onClick={() => onLearnMore(trial.nctId)}
        >
          Learn More
        </button>
      </div>
    </article>
  );
};

export default ClinicalTrialsResult;
